/**
 * Twitter Data Parsers
 *
 * Twitter exports data as JavaScript files:
 * window.YTD.{name}.part0 = [...]
 *
 * We extract the JSON array and parse it.
 */

import type {
  TwitterProfile,
  TwitterAccount,
  Tweet,
  DirectMessage,
  Like,
  TwitterData,
} from './types.js';

/**
 * Extract JSON array from Twitter's JS format
 * Format: window.YTD.xxx.part0 = [...]
 */
function extractJSON(content: string): unknown[] {
  // Find the start of the array (first '[')
  const startIndex = content.indexOf('[');
  if (startIndex === -1) return [];

  // Extract from '[' to the end
  const jsonStr = content.substring(startIndex);

  try {
    return JSON.parse(jsonStr);
  } catch (err) {
    console.error('Failed to parse Twitter JSON:', err);
    return [];
  }
}

/**
 * Parse profile.js
 */
export function parseProfile(content: string): TwitterProfile | null {
  const data = extractJSON(content);
  if (!data.length) return null;

  const item = data[0] as { profile?: { description?: { bio?: string; location?: string; website?: string }; avatarMediaUrl?: string } };
  const profile = item?.profile;
  if (!profile) return null;

  return {
    bio: profile.description?.bio || '',
    location: profile.description?.location || '',
    website: profile.description?.website || '',
    avatarUrl: profile.avatarMediaUrl,
  };
}

/**
 * Parse account.js
 */
export function parseAccount(content: string): TwitterAccount | null {
  const data = extractJSON(content);
  if (!data.length) return null;

  const item = data[0] as {
    account?: {
      username?: string;
      accountDisplayName?: string;
      accountId?: string;
      email?: string;
      createdAt?: string;
    }
  };
  const account = item?.account;
  if (!account) return null;

  return {
    username: account.username || '',
    displayName: account.accountDisplayName || '',
    accountId: account.accountId || '',
    email: account.email,
    createdAt: account.createdAt ? new Date(account.createdAt) : undefined,
  };
}

/**
 * Parse tweets.js
 */
export function parseTweets(content: string): Tweet[] {
  const data = extractJSON(content);
  const tweets: Tweet[] = [];

  for (const item of data) {
    const tweetData = (item as { tweet?: Record<string, unknown> })?.tweet;
    if (!tweetData) continue;

    const fullText = (tweetData.full_text as string) || '';
    const isRetweet = fullText.startsWith('RT @');
    const isReply = !!(tweetData.in_reply_to_user_id_str);

    // Extract hashtags
    const entities = tweetData.entities as { hashtags?: { text: string }[]; user_mentions?: { screen_name: string }[]; urls?: { expanded_url: string }[] } | undefined;
    const hashtags = (entities?.hashtags || []).map(h => h.text);
    const mentions = (entities?.user_mentions || []).map(m => m.screen_name);
    const urls = (entities?.urls || []).map(u => u.expanded_url);

    tweets.push({
      id: (tweetData.id_str as string) || '',
      text: fullText,
      createdAt: tweetData.created_at ? new Date(tweetData.created_at as string) : new Date(),
      isRetweet,
      isReply,
      replyToUser: (tweetData.in_reply_to_screen_name as string) || undefined,
      favoriteCount: parseInt((tweetData.favorite_count as string) || '0', 10),
      retweetCount: parseInt((tweetData.retweet_count as string) || '0', 10),
      lang: (tweetData.lang as string) || undefined,
      source: (tweetData.source as string) || undefined,
      hashtags,
      mentions,
      urls,
    });
  }

  return tweets;
}

/**
 * Parse direct-messages.js
 */
export function parseDirectMessages(content: string, accountId: string): DirectMessage[] {
  const data = extractJSON(content);
  const messages: DirectMessage[] = [];

  for (const item of data) {
    const conversation = (item as { dmConversation?: { conversationId: string; messages: unknown[] } })?.dmConversation;
    if (!conversation) continue;

    const conversationId = conversation.conversationId;

    for (const msg of conversation.messages) {
      const messageCreate = (msg as { messageCreate?: Record<string, unknown> })?.messageCreate;
      if (!messageCreate) continue;

      const senderId = (messageCreate.senderId as string) || '';

      messages.push({
        id: (messageCreate.id as string) || '',
        conversationId,
        senderId,
        recipientId: (messageCreate.recipientId as string) || '',
        text: (messageCreate.text as string) || '',
        createdAt: messageCreate.createdAt ? new Date(messageCreate.createdAt as string) : new Date(),
        isFromUser: senderId === accountId,
      });
    }
  }

  // Sort by date (oldest first within each conversation)
  messages.sort((a, b) => a.createdAt.getTime() - b.createdAt.getTime());

  return messages;
}

/**
 * Parse like.js
 */
export function parseLikes(content: string): Like[] {
  const data = extractJSON(content);
  const likes: Like[] = [];

  for (const item of data) {
    const likeData = (item as { like?: { tweetId?: string; fullText?: string; expandedUrl?: string } })?.like;
    if (!likeData) continue;

    likes.push({
      tweetId: likeData.tweetId || '',
      fullText: likeData.fullText || '',
      expandedUrl: likeData.expandedUrl,
    });
  }

  return likes;
}

/**
 * Detect if a zip file is a Twitter export
 */
export function isTwitterExport(filenames: string[]): boolean {
  // Twitter exports have a data/ folder with specific JS files
  const hasDataFolder = filenames.some(f => f.startsWith('data/'));
  const hasTweetsJs = filenames.some(f => f === 'data/tweets.js');
  const hasAccountJs = filenames.some(f => f === 'data/account.js');
  const hasManifestJs = filenames.some(f => f === 'data/manifest.js');

  return hasDataFolder && (hasTweetsJs || hasManifestJs) && hasAccountJs;
}

/**
 * Parse all Twitter data from a map of filename -> content
 */
export function parseTwitterData(files: Map<string, string>): TwitterData {
  const data: TwitterData = {
    profile: null,
    account: null,
    tweets: [],
    directMessages: [],
    likes: [],
  };

  // Parse account first (needed for DM parsing)
  const accountContent = files.get('data/account.js');
  if (accountContent) {
    data.account = parseAccount(accountContent);
    console.log('%cParsed Twitter account:', 'color: cyan', data.account?.username);
  }

  // Parse profile
  const profileContent = files.get('data/profile.js');
  if (profileContent) {
    data.profile = parseProfile(profileContent);
    console.log('%cParsed Twitter profile:', 'color: cyan', data.profile?.bio?.substring(0, 50));
  }

  // Parse tweets
  const tweetsContent = files.get('data/tweets.js');
  if (tweetsContent) {
    data.tweets = parseTweets(tweetsContent);
    console.log('%cParsed Twitter tweets:', 'color: magenta; font-weight: bold', data.tweets.length);
  }

  // Parse DMs
  const dmsContent = files.get('data/direct-messages.js');
  if (dmsContent && data.account) {
    data.directMessages = parseDirectMessages(dmsContent, data.account.accountId);
    console.log('%cParsed Twitter DMs:', 'color: magenta; font-weight: bold', data.directMessages.length);
  }

  // Parse likes
  const likesContent = files.get('data/like.js');
  if (likesContent) {
    data.likes = parseLikes(likesContent);
    console.log('%cParsed Twitter likes:', 'color: magenta', data.likes.length);
  }

  return data;
}
